/*
|--------------------------------------------------------------------------
| Component: AttributeManager.jsx
| Author: Moaz
| Editing: Sayed Ahmed
| Description: Allows users to dynamically add/remove key-value product attributes.
|--------------------------------------------------------------------------
*/

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormField from "../../components/ui/FormField";
import { Button } from "react-bootstrap";

export default function AttributeManager({
  typesObject,
  setTypesObject,
  keyInput,
  valueInput,
  setKeyInput,
  setValueInput,
}) {
  const { t } = useTranslation();
  const [showInputs, setShowInputs] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleDelete = (key) => {
    const updatedObject = { ...typesObject };
    delete updatedObject[key];
    setTypesObject(updatedObject);
  };

  const handleAppend = () => {
    if (!keyInput.trim() || !valueInput.trim()) return;

    const normalizedKey = keyInput.trim().toLowerCase();
    const exists = Object.keys(typesObject).some(
      (k) => k.trim().toLowerCase() == normalizedKey
    );

    if (exists) {
      setErrorMsg(t("This key already exists"));
      return;
    }

    setTypesObject((prev) => ({
      ...prev,
      [keyInput.trim()]: valueInput.trim(),
    }));

    setKeyInput("");
    setValueInput("");
    setErrorMsg("");
    setShowInputs(false);
  };

  const hasAttributes = typesObject && Object.keys(typesObject).length > 0;

  return (
    <div className="form-group">
      <label className="block mb-2 fw-semibold">{t("Attributes")}</label>

      {!hasAttributes && (
        <p className="text-center text-muted">{t("Add Product Attributes")}</p>
      )}

      {hasAttributes &&
        Object.entries(typesObject).map(([key, value]) => (
          <div className="row align-items-center mb-2" key={key}>
            <div className="col-sm-2 col-md-3">
              <input type="text" className="form-control" value={key} disabled />
            </div>
            <div className="col-sm-9 col-md-8">
              <input type="text" className="form-control" value={value} disabled />
            </div>
            <div className="col-sm-1 text-center">
              <i
                className="fa-solid fa-trash text-danger fs-5 cursor-pointer hover:scale-110 transition"
                onClick={() => handleDelete(key)}
              />
            </div>
          </div>
        ))}

      {!showInputs ? (
        <div className="text-end">
          <Button
            variant="outline-primary"
            className="mt-2"
            onClick={() => {
              setErrorMsg("");
              setShowInputs(true);
            }}
          >
            {t("Add Attribute")}
          </Button>
        </div>
      ) : (
        <>
          <div className="row mb-3">
            <div className="col-sm-3 col-md-4">
              <FormField
                label={t("Key")}
                name="dynamic_key"
                value={keyInput}
                onChange={(e) => {
                  setErrorMsg("");
                  setKeyInput(e.target.value);
                }}
              />
            </div>
            <div className="col-sm-9 col-md-8">
              <FormField
                label={t("Value")}
                name="dynamic_value"
                value={valueInput}
                onChange={(e) => {
                  setErrorMsg("");
                  setValueInput(e.target.value);
                }}
              />
            </div>
          </div>

          {errorMsg && <div className="text-danger mb-2">{errorMsg}</div>}

          <div className="text-end d-flex gap-2 justify-content-end">
            <Button
              variant="secondary"
              onClick={() => {
                setKeyInput("");
                setValueInput("");
                setErrorMsg("");
                setShowInputs(false);
              }}
            >
              {t("Cancel")}
            </Button>
            <Button variant="primary" onClick={handleAppend}>
              {t("Append")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
